'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import posthog from 'posthog-js'
import { submitNotMemberRequest, resolveLocationLink } from './actions'

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type Lang = 'en' | 'es'

type ServiceKey =
  | 'welding'
  | 'electricity'
  | 'quick_fixes'
  | 'plumbing'
  | 'installations'
  | 'ac'

interface Service {
  key:    ServiceKey
  label:  Record<Lang, string>
  desc:   Record<Lang, string>
}

// Inline service icons. Lucide-style 1.75 stroke, 22px square — same
// drawing register as the rest of the app (see /dashboard category
// icons in services/page.tsx). Replaces the emoji set we shipped on
// v1 of this form (DESIGN.md bans emoji-as-iconography).
type IconProps = { className?: string }
const Svg = (d: React.ReactNode) => ({ className }: IconProps) => (
  <svg className={className} aria-hidden="true" width="22" height="22" viewBox="0 0 24 24"
       fill="none" stroke="currentColor" strokeWidth="1.75"
       strokeLinecap="round" strokeLinejoin="round">{d}</svg>
)

const SERVICE_ICON: Record<ServiceKey, ReturnType<typeof Svg>> = {
  welding:       Svg(<><path d="M14.7 6.3a4.5 4.5 0 0 0-5.96 5.96L3 18l3 3 5.74-5.74a4.5 4.5 0 0 0 5.96-5.96l-2.83 2.83-2.12-2.12 2.83-2.83Z"/></>),
  electricity:   Svg(<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>),
  quick_fixes:   Svg(<><path d="M14.7 6.3a4.5 4.5 0 0 0-5.96 5.96L3 18l3 3 5.74-5.74a4.5 4.5 0 0 0 5.96-5.96l-2.83 2.83-2.12-2.12 2.83-2.83Z"/><path d="M5 21h6"/></>),
  plumbing:      Svg(<><path d="M3 12h4l2-3 4 6 2-3h6"/><path d="M5 12V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4"/></>),
  installations: Svg(<><rect x="3" y="3" width="18" height="6" rx="1"/><rect x="3" y="15" width="18" height="6" rx="1"/><path d="M9 6h.01M15 6h.01M9 18h.01M15 18h.01"/></>),
  ac:            Svg(<><path d="M12 2v20M4.93 4.93l14.14 14.14M2 12h20M4.93 19.07 19.07 4.93"/></>),
}

const SERVICES: Service[] = [
  {
    key: 'welding',
    label: { en: 'Welding',       es: 'Soldadura' },
    desc:  { en: 'Gates, railings, structural mild steel.',
             es: 'Portones, barandas, estructural en acero.' },
  },
  {
    key: 'electricity',
    label: { en: 'Electrical',    es: 'Eléctrico' },
    desc:  { en: 'Outlets, breakers, lighting, short circuits, rewiring.',
             es: 'Tomacorrientes, breakers, iluminación, cortos, recableado.' },
  },
  {
    key: 'quick_fixes',
    label: { en: 'Quick Fixes',   es: 'Arreglos Rápidos' },
    desc:  { en: 'Doors, locks, shelves, squeaks, small jobs around the house.',
             es: 'Puertas, cerraduras, repisas, rechinidos, trabajos pequeños.' },
  },
  {
    key: 'plumbing',
    label: { en: 'Plumbing',      es: 'Plomería' },
    desc:  { en: 'Leaks, pressure, toilets, water heaters, drain clearing.',
             es: 'Fugas, presión, sanitarios, calentadores, destape de drenajes.' },
  },
  {
    key: 'installations',
    label: { en: 'Installations', es: 'Instalaciones' },
    desc:  { en: 'Appliances, fixtures, ceiling fans, mounts, curtain rods.',
             es: 'Electrodomésticos, fijaciones, abanicos, soportes, cortineros.' },
  },
  {
    key: 'ac',
    label: { en: 'AC Maintenance', es: 'Mantenimiento A/C' },
    desc:  { en: 'Cleaning, gas recharge, drainage, diagnostics, repairs.',
             es: 'Limpieza, recarga de gas, drenaje, diagnóstico, reparación.' },
  },
]

/* ------------------------------------------------------------------ */
/* i18n                                                                */
/* ------------------------------------------------------------------ */

interface Copy {
  brandBack:    string
  eyebrow:      string
  h1a:          string
  h1b:          string
  h1c:          string
  lede:         string
  pick:         string
  pickSub:      string
  details:      string
  name:         string
  email:        string
  phone:        string
  problem:      string
  problemPH:    string
  location:     string
  locationPH:   string
  mapsLink:     string
  mapsLinkPH:   string
  mapsHint:     string
  mapsChecking: string
  mapsInArea:   (place: string | null, km: number | null) => string
  mapsOutArea:  string
  mapsBadLink:  string
  submit:       string
  sending:      string
  quoteShowFix: string
  footerNote:   string
  privacyPre:   string
  privacyLink:  string
  successH:     string
  successP:     string
  successCTA:   string
  errorBadge:   string
  reqField:     string
  invalidEmail: string
  pickAtLeast:  string
  legal:        string
}

const T: Record<Lang, Copy> = {
  en: {
    brandBack: '← back to main site',
    eyebrow:   'FOR NON-MEMBERS',
    h1a:       "Not a member?",
    h1b:       "No problem.",
    h1c:       "We fix it.",
    lede:
      "Tell us what's broken and we'll come take a look. Welding, electrical, plumbing, installations, AC, quick fixes, across Nosara, Guiones and Pelada.",
    pick:      'What do you need help with?',
    pickSub:   'Select one or more',
    details:   'Your details',
    name:      'Full name',
    email:     'Email',
    phone:     'Phone (WhatsApp)',
    problem:   "What's the problem?",
    problemPH: 'e.g. Kitchen tap is leaking, the AC in the master bedroom stopped cooling, need a security gate welded…',
    location:  'Where are you?',
    locationPH:'e.g. Playa Guiones, 200m east of Harmony Hotel',
    mapsLink:  'Location pin — optional',
    mapsLinkPH:'Paste a Google Maps or Waze link',
    mapsHint:  'Share your location from Google Maps or Waze and paste the link here. We check coverage instantly and the technician arrives without calling for directions.',
    mapsChecking: 'Checking location…',
    mapsInArea: (place, km) =>
      `You're in our service area${place ? ` — near ${place}${km !== null ? ` (~${km} km)` : ''}` : ''}.`,
    mapsOutArea:
      "That pin looks outside our usual service area. You can still send the request — we'll confirm by WhatsApp if we can make it out there.",
    mapsBadLink:
      "We couldn't read coordinates from that link. Make sure it's a shared location from Google Maps or Waze — or just leave it blank.",
    submit:    'Send and we will contact you',
    sending:   'Sending…',
    quoteShowFix: 'We quote. We show. We fix.',
    footerNote:
      'We usually reply within a few hours during business days.',
    privacyPre:  'We only use your details to reply to this request. See our ',
    privacyLink: 'Privacy Policy',
    successH:  'Got it. Thanks!',
    successP:
      'We received your request and one of our team will reach out on WhatsApp shortly. Usually within a few hours.',
    successCTA:'Send another',
    errorBadge:'Something went wrong',
    reqField:  'This field is required',
    invalidEmail: 'Please enter a valid email',
    pickAtLeast: 'Select at least one service',
    legal:     '© The Repair Club · Nosara, Guanacaste, Costa Rica',
  },
  es: {
    brandBack: '← volver al sitio principal',
    eyebrow:   'PARA NO MIEMBROS',
    h1a:       '¿No eres miembro?',
    h1b:       'No hay problema.',
    h1c:       'Lo arreglamos.',
    lede:
      'Cuéntanos qué se dañó y pasamos a revisarlo. Soldadura, eléctrico, plomería, instalaciones, A/C, arreglos rápidos, en Nosara, Guiones y Pelada.',
    pick:      '¿En qué necesitas ayuda?',
    pickSub:   'Selecciona uno o más',
    details:   'Tus datos',
    name:      'Nombre completo',
    email:     'Correo',
    phone:     'Teléfono (WhatsApp)',
    problem:   '¿Cuál es el problema?',
    problemPH: 'ej. El grifo de la cocina gotea, el A/C del cuarto dejó de enfriar, necesito soldar un portón…',
    location:  '¿Dónde te encontramos?',
    locationPH:'ej. Playa Guiones, 200m al este del Harmony Hotel',
    mapsLink:  'Pin de ubicación — opcional',
    mapsLinkPH:'Pega un link de Google Maps o Waze',
    mapsHint:  'Comparte tu ubicación desde Google Maps o Waze y pega el link aquí. Verificamos cobertura al instante y el técnico llega sin pedir señas.',
    mapsChecking: 'Verificando ubicación…',
    mapsInArea: (place, km) =>
      `Estás dentro de nuestra zona de servicio${place ? ` — cerca de ${place}${km !== null ? ` (~${km} km)` : ''}` : ''}.`,
    mapsOutArea:
      'Ese pin parece estar fuera de nuestra zona habitual. Igual puedes enviar la solicitud — te confirmamos por WhatsApp si podemos llegar.',
    mapsBadLink:
      'No pudimos leer coordenadas de ese link. Asegúrate de compartir la ubicación desde Google Maps o Waze — o déjalo en blanco.',
    submit:    'Enviar y te contactamos',
    sending:   'Enviando…',
    quoteShowFix: 'Cotizamos. Llegamos. Reparamos.',
    footerNote:
      'Normalmente respondemos en pocas horas en días laborales.',
    privacyPre:  'Solo usamos tus datos para responder esta solicitud. Ver nuestra ',
    privacyLink: 'Política de privacidad',
    successH:  '¡Listo, gracias!',
    successP:
      'Recibimos tu solicitud y alguien del equipo te escribirá por WhatsApp muy pronto. Usualmente en pocas horas.',
    successCTA:'Enviar otra',
    errorBadge:'Algo salió mal',
    reqField:  'Este campo es obligatorio',
    invalidEmail: 'Por favor ingresa un correo válido',
    pickAtLeast: 'Selecciona al menos un servicio',
    legal:     '© The Repair Club · Nosara, Guanacaste, Costa Rica',
  },
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function NotMemberForm() {
  const [lang, setLang] = useState<Lang>('en')
  const t = T[lang]

  const [selected, setSelected] = useState<Record<ServiceKey, boolean>>({
    welding: false,
    electricity: false,
    quick_fixes: false,
    plumbing: false,
    installations: false,
    ac: false,
  })

  const [name, setName]         = useState('')
  const [email, setEmail]       = useState('')
  const [phone, setPhone]       = useState('')
  const [problem, setProblem]   = useState('')
  const [location, setLocation] = useState('')

  // Optional maps-link pin: the pasted URL, the resolution state, and
  // the resolved result (coords + coverage) that rides along on submit.
  const [mapsUrl, setMapsUrl]   = useState('')
  const [mapsStatus, setMapsStatus] = useState<'idle' | 'checking' | 'in' | 'out' | 'bad'>('idle')
  const [pin, setPin] = useState<{ lat: number; lng: number; closestName: string | null; closestKm: number | null } | null>(null)

  async function checkMapsLink(url: string) {
    const trimmed = url.trim()
    if (!trimmed) { setMapsStatus('idle'); setPin(null); return }
    setMapsStatus('checking')
    try {
      const res = await resolveLocationLink(trimmed)
      if (res.ok) {
        setPin({ lat: res.lat, lng: res.lng, closestName: res.closestName, closestKm: res.closestKm })
        setMapsStatus(res.inCoverage ? 'in' : 'out')
      } else {
        setPin(null)
        const limited = 'error' in res && res.error === 'rate_limited'
        setMapsStatus(limited ? 'idle' : 'bad')
      }
    } catch {
      setPin(null)
      setMapsStatus('bad')
    }
  }

  const [loading, setLoading]     = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError]         = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const selectedKeys = useMemo(
    () => (Object.keys(selected) as ServiceKey[]).filter(k => selected[k]),
    [selected]
  )

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  function toggle(k: ServiceKey) {
    setSelected(prev => ({ ...prev, [k]: !prev[k] }))
  }

  function fieldErr(v: string) {
    return showErrors && !v.trim() ? t.reqField : null
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setShowErrors(true)

    const missing = !name.trim() || !email.trim() || !phone.trim() || !problem.trim() || !location.trim()
    if (missing || !emailValid || selectedKeys.length === 0) {
      // scroll to first problem section
      const id = selectedKeys.length === 0 ? 'services' : 'details'
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    setLoading(true)
    try {
      const res = await submitNotMemberRequest({
        name:      name.trim(),
        email:     email.trim(),
        phone:     phone.trim(),
        problem:   problem.trim(),
        location:  location.trim(),
        services:  selectedKeys,
        lang,
        latitude:  pin?.lat ?? null,
        longitude: pin?.lng ?? null,
      })
      if (res.ok) {
        // Analytics: tie this anonymous browsing session to the lead's
        // email so the server-fired `lead_created` event lines up on
        // the same person profile.
        if (process.env.NEXT_PUBLIC_POSTHOG_KEY && email.trim()) {
          try {
            posthog.alias(email.trim())
            posthog.capture('notmember_form_submitted', {
              services: selectedKeys,
              lang,
            })
          } catch {}
        }
        setSubmitted(true)
      } else {
        // res is narrowed to the error branch
        setError((res as { ok: false; error: string }).error)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  function resetForm() {
    setSubmitted(false)
    setShowErrors(false)
    setError(null)
    setName('')
    setEmail('')
    setPhone('')
    setProblem('')
    setLocation('')
    setMapsUrl('')
    setMapsStatus('idle')
    setPin(null)
    setSelected({
      welding: false, electricity: false, quick_fixes: false,
      plumbing: false, installations: false, ac: false,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /* ---------------- Success screen ---------------- */

  if (submitted) {
    return (
      <main className="min-h-screen bg-trc-cream flex flex-col">
        <TopBar lang={lang} onLang={setLang} t={t} />
        <section className="flex-1 flex items-center justify-center px-6 py-20">
          <div
            className="bg-white rounded-[14px] p-10 sm:p-14 max-w-lg w-full text-center"
            style={{ boxShadow: '0 12px 48px rgba(0,0,0,0.12)' }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center bg-trc-blue-pale">
              <svg className="w-10 h-10 text-trc-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-trc-navy mb-3 tracking-tight">
              {t.successH}
            </h1>
            <p className="text-trc-ash text-[15px] leading-relaxed mb-8 max-w-sm mx-auto">
              {t.successP}
            </p>
            <p className="text-[12px] font-bold text-trc-blue uppercase tracking-[2px] mb-6">
              {t.quoteShowFix}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={resetForm}
                className="px-6 py-3 bg-trc-blue hover:bg-trc-blue-mid text-white font-bold rounded-xl text-sm transition-all"
              >
                {t.successCTA}
              </button>
              <Link
                href="/"
                className="px-6 py-3 bg-white border border-trc-ash-line hover:border-trc-blue text-trc-navy font-semibold rounded-xl text-sm transition-all text-center"
              >
                {t.brandBack}
              </Link>
            </div>
          </div>
        </section>
        <Footer t={t} />
      </main>
    )
  }

  /* ---------------- Main form ---------------- */

  return (
    <main className="min-h-screen bg-trc-cream">
      <TopBar lang={lang} onLang={setLang} t={t} />

      {/* HERO — flat blue-pale wash. Drops the v1 gradient to match
          the rest of the TRC system, which uses flat surfaces and
          differentiates sections through tone rather than transitions. */}
      <section className="px-6 pt-20 pb-14 sm:pt-28 sm:pb-20 bg-trc-blue-pale">
        <div className="max-w-3xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 bg-trc-blue-pale text-trc-blue
                       px-4 py-1.5 rounded-full text-[12px] font-bold tracking-[1.5px]
                       uppercase mb-6 border border-trc-blue/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-trc-blue" />
            {t.eyebrow}
          </span>
          <h1 className="text-[2.2rem] sm:text-[3.4rem] font-black leading-[1.05] tracking-tight text-trc-navy mb-5">
            {t.h1a}
            <br />
            {t.h1b} <span className="text-trc-blue">{t.h1c}</span>
          </h1>
          <p className="text-[15px] sm:text-[17px] text-trc-ash leading-relaxed max-w-xl mx-auto">
            {t.lede}
          </p>
        </div>
      </section>

      {/* FORM */}
      <form
        onSubmit={handleSubmit}
        className="px-6 py-14 max-w-3xl mx-auto space-y-10"
      >
        {/* Services */}
        <section id="services" className="scroll-mt-24">
          <SectionHeader index="01" title={t.pick} sub={t.pickSub} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SERVICES.map(s => {
              const on = selected[s.key]
              return (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => toggle(s.key)}
                  className={`text-left bg-white rounded-[14px] p-5 border-2 transition-all select-none
                              ${on ? 'border-trc-blue' : 'border-transparent hover:border-trc-ash-line'}`}
                  style={{
                    boxShadow: on
                      ? '0 4px 24px rgba(26,79,138,0.15)'
                      : '0 4px 24px rgba(0,0,0,0.06)',
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center
                                     flex-shrink-0 mt-1 transition-all
                                     ${on ? 'bg-trc-blue border-trc-blue' : 'border-trc-ash-line'}`}>
                      {on && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 text-trc-blue">
                        {(() => {
                          const Icon = SERVICE_ICON[s.key]
                          return <Icon className="" />
                        })()}
                        <span className="font-bold text-trc-navy text-[15px]">{s.label[lang]}</span>
                      </div>
                      <p className="text-[13px] text-trc-ash leading-relaxed">{s.desc[lang]}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
          {showErrors && selectedKeys.length === 0 && (
            <p className="mt-3 text-[13px] text-trc-signal-red font-semibold">{t.pickAtLeast}</p>
          )}
        </section>

        {/* Details */}
        <section id="details" className="scroll-mt-24">
          <SectionHeader index="02" title={t.details} />
          <div className="bg-white rounded-[14px] p-6 sm:p-8 space-y-5"
               style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <Field
              label={t.name}
              value={name}
              onChange={setName}
              error={fieldErr(name)}
              autoComplete="name"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label={t.email}
                type="email"
                value={email}
                onChange={setEmail}
                error={
                  showErrors && !email.trim()
                    ? t.reqField
                    : showErrors && !emailValid
                    ? t.invalidEmail
                    : null
                }
                autoComplete="email"
                inputMode="email"
              />
              <Field
                label={t.phone}
                type="tel"
                value={phone}
                onChange={setPhone}
                error={fieldErr(phone)}
                autoComplete="tel"
                inputMode="tel"
                placeholder="+506 …"
              />
            </div>
            <TextAreaField
              label={t.problem}
              value={problem}
              onChange={setProblem}
              rows={4}
              placeholder={t.problemPH}
              error={fieldErr(problem)}
            />
            <Field
              label={t.location}
              value={location}
              onChange={setLocation}
              error={fieldErr(location)}
              placeholder={t.locationPH}
            />

            {/* Optional Waze / Google Maps pin — resolves to GPS coords
                server-side and shows coverage feedback instantly. */}
            <div>
              <label className="block text-[13px] font-bold text-trc-navy mb-1.5">
                {t.mapsLink}
              </label>
              <input
                type="url"
                value={mapsUrl}
                onChange={e => {
                  setMapsUrl(e.target.value)
                  if (!e.target.value.trim()) { setMapsStatus('idle'); setPin(null) }
                }}
                onBlur={() => checkMapsLink(mapsUrl)}
                onPaste={e => {
                  const pasted = e.clipboardData.getData('text')
                  if (pasted.trim()) checkMapsLink(pasted)
                }}
                placeholder={t.mapsLinkPH}
                inputMode="url"
                autoComplete="off"
                className="w-full px-4 py-3 rounded-[10px] border-2 border-trc-ash-line bg-white text-[15px] text-trc-navy placeholder:text-trc-ash/60 focus:border-trc-blue focus:outline-none transition-colors"
              />
              {mapsStatus === 'idle' && (
                <p className="mt-1.5 text-[12px] text-trc-ash leading-relaxed">{t.mapsHint}</p>
              )}
              {mapsStatus === 'checking' && (
                <p className="mt-1.5 text-[12px] font-semibold text-trc-blue">{t.mapsChecking}</p>
              )}
              {mapsStatus === 'in' && (
                <p className="mt-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  ✓ {t.mapsInArea(pin?.closestName ?? null, pin?.closestKm ?? null)}
                </p>
              )}
              {mapsStatus === 'out' && (
                <p className="mt-1.5 text-[12px] font-semibold text-trc-signal-gold bg-trc-signal-gold/10 border border-trc-signal-gold/25 rounded-lg px-3 py-2">
                  {t.mapsOutArea}
                </p>
              )}
              {mapsStatus === 'bad' && (
                <p className="mt-1.5 text-[12px] font-semibold text-trc-ash bg-trc-cream border border-trc-ash-line rounded-lg px-3 py-2">
                  {t.mapsBadLink}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Tagline */}
        <div className="text-center py-4">
          <p className="text-[12px] font-bold text-trc-blue uppercase tracking-[3px] mb-2">
            {t.quoteShowFix}
          </p>
          <div className="w-10 h-[2px] bg-trc-blue mx-auto rounded-full opacity-60" />
        </div>

        {/* Submit */}
        <div className="space-y-3">
          {error && (
            <div className="bg-trc-signal-red/5 border border-trc-signal-red/30 rounded-[10px] px-4 py-3 text-[13px]">
              <p className="font-bold text-trc-signal-red mb-0.5">{t.errorBadge}</p>
              <p className="text-trc-signal-red">{error}</p>
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-trc-blue hover:bg-trc-blue-mid text-white font-black
                       rounded-xl text-[15px] tracking-tight transition-all
                       hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(26,79,138,0.25)]
                       disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? t.sending : t.submit}
          </button>
          <p className="text-center text-[12px] text-trc-ash leading-relaxed max-w-md mx-auto pt-2">
            {t.footerNote}
          </p>
          <p className="text-center text-[12px] text-trc-ash leading-relaxed max-w-md mx-auto pt-1">
            {t.privacyPre}
            <a href="/privacy" className="text-trc-blue underline decoration-trc-blue/40 underline-offset-2 hover:decoration-trc-blue">
              {t.privacyLink}
            </a>
            {' · '}
            <a href="https://wa.me/50671588597" className="text-trc-blue underline decoration-trc-blue/40 underline-offset-2 hover:decoration-trc-blue">
              WhatsApp
            </a>
          </p>
        </div>
      </form>

      <Footer t={t} />
    </main>
  )
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function TopBar({
  lang, onLang, t,
}: { lang: Lang; onLang: (l: Lang) => void; t: Copy }) {
  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 h-[68px] flex items-center justify-between
                 px-6 sm:px-[5%] border-b border-black/5"
      style={{
        background: 'rgba(242,247,253,0.94)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      <Link href="/" className="inline-flex items-center no-underline">
        <Image
          src="/TRC-Logo.png"
          alt="The Repair Club Nosara"
          width={450}
          height={281}
          style={{ height: 44, width: 'auto' }}
          className="object-contain"
          priority
        />
      </Link>

      <div className="flex items-center gap-4">
        <Link
          href="/"
          className="hidden sm:inline text-[13px] font-medium text-trc-ash hover:text-trc-blue transition-colors"
        >
          {t.brandBack}
        </Link>
        <div className="flex rounded-full border border-black/10 bg-white/70 overflow-hidden text-[11px] font-bold">
          {(['en', 'es'] as const).map(l => (
            <button
              key={l}
              type="button"
              onClick={() => onLang(l)}
              className={`px-3 py-1.5 transition-colors ${
                lang === l
                  ? 'bg-trc-blue text-white'
                  : 'text-trc-ash hover:text-trc-navy'
              }`}
              aria-pressed={lang === l}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </nav>
  )
}

function SectionHeader({ index, title, sub }: { index: string; title: string; sub?: string }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[11px] font-mono font-bold text-trc-blue tracking-widest">
          {index}
        </span>
        <span className="flex-1 h-px bg-black/10" />
      </div>
      <h2 className="text-[22px] sm:text-[26px] font-black text-trc-navy tracking-tight leading-tight">
        {title}
      </h2>
      {sub && <p className="text-[13px] text-trc-ash mt-1">{sub}</p>}
    </div>
  )
}

function Field({
  label, value, onChange, type = 'text',
  error = null, placeholder, autoComplete, inputMode,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  error?: string | null
  placeholder?: string
  autoComplete?: string
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'search' | 'url'
}) {
  return (
    <label className="block">
      <span className="block text-[12px] font-bold text-trc-navy uppercase tracking-[1.5px] mb-2">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        className={`w-full px-4 py-3 rounded-xl border bg-white text-[14px]
                    focus:outline-none focus:border-trc-blue focus:ring-2 focus:ring-trc-blue/15
                    transition-colors ${
                      error ? 'border-trc-signal-red' : 'border-trc-ash-line'
                    }`}
      />
      {error && <p className="mt-1.5 text-[12px] text-trc-signal-red font-semibold">{error}</p>}
    </label>
  )
}

function TextAreaField({
  label, value, onChange, rows = 4, placeholder, error = null,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
  error?: string | null
}) {
  return (
    <label className="block">
      <span className="block text-[12px] font-bold text-trc-navy uppercase tracking-[1.5px] mb-2">
        {label}
      </span>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-xl border bg-white text-[14px]
                    focus:outline-none focus:border-trc-blue focus:ring-2 focus:ring-trc-blue/15
                    transition-colors resize-y min-h-[110px] ${
                      error ? 'border-trc-signal-red' : 'border-trc-ash-line'
                    }`}
      />
      {error && <p className="mt-1.5 text-[12px] text-trc-signal-red font-semibold">{error}</p>}
    </label>
  )
}

function Footer({ t }: { t: Copy }) {
  return (
    <footer className="border-t border-black/5 bg-white">
      <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[12px] text-trc-ash">{t.legal}</p>
        <p className="text-[11px] font-bold text-trc-blue uppercase tracking-[2px]">
          {t.quoteShowFix}
        </p>
      </div>
    </footer>
  )
}
